import React, { useCallback, useEffect, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import AppBar from '../components/AppBar'
import Avatar from '../components/Avatar'
import Gap from '../components/Gap'
import LoadingSpinner from '../components/LoadingSpinner'
import ConfirmDialog from '../components/ConfirmDialog'
import { goBackOrHome } from '../utils/navigation'
import { toastUtil } from '../utils/toast'
import { userApi, BlockedUser } from '../api/user'
import { invalidateReviewCaches } from '../utils/homeFeedCache'

/**
 * 차단한 사용자 목록. 차단을 풀 수 있는 유일한 자리다.
 *
 * 프로필에서는 차단만 할 수 있다 — 이미 차단한 사람의 프로필은 서버가 404로
 * 답하기 때문에 그 화면에서 해제 버튼을 둘 수가 없다. 그래서 여기가 필요하다.
 *
 * 나를 차단한 사람은 여기 없다. 내가 풀 수 있는 것은 내가 건 것뿐이고,
 * 목록에 보이면 "누가 나를 차단했는지" 알려 주는 셈이 된다.
 */
const BlockedUsers: React.FC = () => {
  const navigate = useNavigate()
  const [users, setUsers] = useState<BlockedUser[]>([])
  const [isLoading, setIsLoading] = useState(true)
  const [pendingUnblock, setPendingUnblock] = useState<BlockedUser | null>(null)
  const [isUnblocking, setIsUnblocking] = useState(false)

  const load = useCallback(async () => {
    try {
      const response = await userApi.getBlockedUsers()
      setUsers(response.users)
    } catch (error) {
      console.error('차단 목록 로드 실패:', error)
    } finally {
      setIsLoading(false)
    }
  }, [])

  useEffect(() => {
    load()
  }, [load])

  const handleUnblock = async () => {
    if (!pendingUnblock || isUnblocking) return

    setIsUnblocking(true)
    try {
      await userApi.unblockUser(pendingUnblock.id)
      // 목록을 다시 읽지 않고 그 줄만 뺀다 — 눌렀을 때 바로 사라지는 편이 낫다
      setUsers((prev) => prev.filter((u) => u.id !== pendingUnblock.id))
      /*
       * 피드 캐시를 버린다. 차단할 때와 같은 이유인데 방향이 반대다 —
       * 풀었으면 그 사람 기록이 다시 보여야 하는데, 스냅샷은 안 보이던
       * 시절의 것이라 그대로 두면 계속 빠져 있다.
       */
      invalidateReviewCaches()
      setPendingUnblock(null)
      toastUtil.success('차단이 해제되었습니다')
    } catch (error) {
      console.error('차단 해제 실패:', error)
    } finally {
      setIsUnblocking(false)
    }
  }

  return (
    <div className="w-full h-full flex flex-col bg-backgroundColorBg2">
      <AppBar
        title="차단한 사용자"
        onBack={() => goBackOrHome(navigate)}
        isBottomBorder
      />

      {isLoading ? (
        <div className="flex-1 flex items-center justify-center empty-state-center">
          <LoadingSpinner />
        </div>
      ) : users.length === 0 ? (
        /*
          비어 있을 때. 기기 화면 중앙에 오도록 자리를 꽉 채우는 요소에
          empty-state-center 를 건다 (index.css 설명 참고).
        */
        <div className="flex-1 relative">
          <div className="absolute inset-0 flex flex-col items-center justify-center empty-state-center">
            <span className="text-style-body2 text-textColorText3">
              차단한 사용자가 없습니다
            </span>
          </div>
        </div>
      ) : (
        <div className="app-container flex-1 overflow-y-auto scrollbar-hide">
          <Gap height={8} />

          {users.map((user) => (
            <div
              key={user.id}
              className="w-full px-20px py-12px flex items-center"
            >
              <Avatar
                nickname={user.username || user.nickname}
                profileImage={user.profileImage}
                size={48}
              />

              <Gap width={12} />

              <div className="flex-1 min-w-0 flex flex-col">
                <span className="font-pretendard font-bold text-14px text-brown-80%-main text-ellipsis-1">
                  {user.username || user.nickname}
                </span>
                {user.username && user.nickname !== user.username && (
                  <span className="text-style-captionM text-textColorText3 text-ellipsis-1">
                    {user.nickname}
                  </span>
                )}
              </div>

              <Gap width={8} />

              <button
                type="button"
                onClick={() => setPendingUnblock(user)}
                className="shrink-0 py-8px px-16px rounded-full border border-brandPrimary text-style-button2 text-brown-80%-main hover:opacity-70"
              >
                차단 해제
              </button>
            </div>
          ))}

          <Gap height={24} />
        </div>
      )}

      <ConfirmDialog
        isOpen={pendingUnblock !== null}
        title={`${pendingUnblock?.username || pendingUnblock?.nickname || ''}님의 차단을 해제하시겠어요?`}
        message="끊어진 팔로우는 되살아나지 않습니다"
        confirmText={isUnblocking ? '해제 중...' : '차단 해제'}
        cancelText="취소"
        onCancel={() => setPendingUnblock(null)}
        onConfirm={handleUnblock}
      />
    </div>
  )
}

export default BlockedUsers
